import { IconButton, JsonField, NumberField, PropRow, SelectField, TextField, Toggle } from '../ui/controls.js';
import { MotionField } from './BlendTreeEditor.js';
import {
  conditionLabel,
  type AnimEvent,
  type BlendTree,
  type ControllerDoc,
  type Layer,
  type State,
  type TransitionRef,
} from './controller-doc.js';

/** The selected state: name, motion (clip / blend tree), playback, events, outgoing transitions. */
export function StateInspector({
  doc,
  state,
  full,
  isDefault,
  outgoing,
  mutate,
  onRename,
  onSetDefault,
  onDelete,
  onSelectTransition,
}: {
  doc: ControllerDoc;
  state: State;
  full: string;
  isDefault: boolean;
  outgoing: TransitionRef[];
  mutate: (fn: (s: State) => void) => void;
  onRename: (name: string) => void;
  onSetDefault: () => void;
  onDelete: () => void;
  onSelectTransition: (ref: TransitionRef) => void;
}) {
  const params = doc.parameters;
  const numeric = params.filter((p) => p.type === 'float' || p.type === 'int').map((p) => p.name);
  const bools = params.filter((p) => p.type === 'bool').map((p) => p.name);
  const optional = (list: string[]) => [{ value: '', label: '(none)' }, ...list.map((n) => ({ value: n, label: n }))];
  const events = state.events ?? [];

  return (
    <div className="anim-inspector-body">
      <div className="anim-inspector-title">
        <span className="mono">{full}</span>
        {isDefault ? <span className="anim-badge">default</span> : <button type="button" className="anim-chip" onClick={onSetDefault}>Set as default</button>}
        <IconButton icon="trash" title="Delete state" onClick={onDelete} />
      </div>
      <PropRow label="Name">
        <TextField value={state.name} onChange={(v) => v.trim() && v.trim() !== state.name && !v.includes('/') && onRename(v.trim())} />
      </PropRow>
      <PropRow label="Motion" hint="A clip (models/anims.glb#Clip or a clip embedded in the model) or a blend tree">
        <MotionField
          motion={state.motion}
          params={params}
          onSet={(m) => mutate((s) => (m === undefined ? delete s.motion : (s.motion = m)))}
          mutateTree={(fn) => mutate((s) => fn(s.motion as BlendTree))}
        />
      </PropRow>
      <PropRow label="Speed">
        <NumberField value={state.speed ?? 1} step={0.05} onChange={(v) => mutate((s) => (v === 1 ? delete s.speed : (s.speed = v)))} />
      </PropRow>
      <PropRow label="Speed parameter" hint="Float parameter multiplied into Speed">
        <SelectField value={state.speedParameter ?? ''} options={optional(numeric)} onChange={(v) => mutate((s) => (v ? (s.speedParameter = v) : delete s.speedParameter))} />
      </PropRow>
      <PropRow label="Loop">
        <Toggle checked={state.loop !== false} onChange={(v) => mutate((s) => (v ? delete s.loop : (s.loop = false)))} />
      </PropRow>
      <PropRow label="Mirror" hint="Play left/right mirrored">
        <Toggle checked={state.mirror === true} onChange={(v) => mutate((s) => (v ? (s.mirror = true) : delete s.mirror))} />
      </PropRow>
      <PropRow label="Mirror parameter">
        <SelectField value={state.mirrorParameter ?? ''} options={optional(bools)} onChange={(v) => mutate((s) => (v ? (s.mirrorParameter = v) : delete s.mirrorParameter))} />
      </PropRow>
      <PropRow label="Cycle offset" hint="Normalized phase offset">
        <NumberField value={state.cycleOffset ?? 0} step={0.05} onChange={(v) => mutate((s) => (v ? (s.cycleOffset = v) : delete s.cycleOffset))} />
      </PropRow>
      <PropRow label="Cycle offset parameter">
        <SelectField
          value={state.cycleOffsetParameter ?? ''}
          options={optional(numeric)}
          onChange={(v) => mutate((s) => (v ? (s.cycleOffsetParameter = v) : delete s.cycleOffsetParameter))}
        />
      </PropRow>
      <PropRow label="Tag">
        <TextField value={state.tag ?? ''} onChange={(v) => mutate((s) => (v ? (s.tag = v) : delete s.tag))} />
      </PropRow>

      <div className="anim-section-head">
        <span>Events</span>
        <button
          type="button"
          className="anim-chip"
          onClick={() => mutate((s) => void (s.events = [...(s.events ?? []), { time: 0.5, function: 'on_event' } satisfies AnimEvent]))}
        >
          + event
        </button>
      </div>
      {events.length === 0 && <p className="muted anim-hint">Events call on_animation_event(name, data) on the animator entity's script.</p>}
      {events.map((ev, i) => (
        <div key={i} className="anim-event">
          <NumberField label="t" title="Normalized time 0..1" value={ev.time} step={0.05} min={0} max={1} onChange={(v) => mutate((s) => void (s.events![i]!.time = v))} />
          <TextField mono value={ev.function} placeholder="name" onChange={(v) => mutate((s) => void (s.events![i]!.function = v))} />
          <JsonField
            rows={1}
            value={ev.args}
            onChange={(v) => mutate((s) => (v === null || v === undefined ? delete s.events![i]!.args : (s.events![i]!.args = v)))}
          />
          <IconButton
            icon="x"
            title="Remove event"
            onClick={() =>
              mutate((s) => {
                s.events!.splice(i, 1);
                if (!s.events!.length) delete s.events;
              })
            }
          />
        </div>
      ))}

      <div className="anim-section-head">
        <span>Transitions</span>
      </div>
      {outgoing.length === 0 && <p className="muted anim-hint">Drag from the state's handle onto another state to add one.</p>}
      {outgoing.map((r, i) => (
        <button key={i} type="button" className="anim-trans-row" onClick={() => onSelectTransition(r)}>
          <span className="mono">→ {r.to}</span>
          <span className="muted">
            {[...(r.t.conditions ?? []).map((c) => conditionLabel(c, params)), r.t.hasExitTime ? `exit ${r.t.exitTime ?? 1}` : ''].filter(Boolean).join(', ') || 'immediate'}
          </span>
        </button>
      ))}
    </div>
  );
}

/** The current layer's settings. */
export function LayerInspector({
  doc,
  layer,
  index,
  mutate,
  onRename,
  onDelete,
}: {
  doc: ControllerDoc;
  layer: Layer;
  index: number;
  mutate: (fn: (l: Layer) => void) => void;
  onRename: (name: string) => void;
  onDelete: () => void;
}) {
  const others = doc.layers.filter((_, i) => i !== index).map((l) => l.name);
  return (
    <div className="anim-inspector-body">
      <div className="anim-inspector-title">
        <span>Layer</span>
        <span className="mono">{layer.name}</span>
        {doc.layers.length > 1 && <IconButton icon="trash" title="Delete layer" onClick={onDelete} />}
      </div>
      <PropRow label="Name">
        <TextField value={layer.name} onChange={(v) => v.trim() && onRename(v.trim())} />
      </PropRow>
      <PropRow label="Weight" hint={index === 0 ? 'The first layer always has weight 1' : '0..1; scripts: set_layer_weight'}>
        <NumberField value={layer.weight ?? 1} step={0.05} min={0} max={1} onChange={(v) => mutate((l) => (v === 1 ? delete l.weight : (l.weight = v)))} />
      </PropRow>
      <PropRow label="Blending" hint="override: replaces lower layers inside the mask; additive: adds motion relative to each clip's first frame">
        <SelectField
          value={layer.blending ?? 'override'}
          options={['override', 'additive']}
          onChange={(v) => mutate((l) => (v === 'override' ? delete l.blending : (l.blending = 'additive')))}
        />
      </PropRow>
      <PropRow label="Mask" hint="Joint names (comma separated), each with its subtree; !Name excludes. Empty = whole body.">
        <TextField
          mono
          value={(layer.mask ?? []).join(', ')}
          placeholder="Spine1, !Neck"
          onChange={(v) =>
            mutate((l) => {
              const m = v.split(',').map((s) => s.trim()).filter(Boolean);
              if (m.length) l.mask = m;
              else delete l.mask;
            })
          }
        />
      </PropRow>
      <PropRow label="Synced layer" hint="Follow another layer's states and timing (this layer's own states are ignored)">
        <SelectField
          value={layer.syncedLayer ?? ''}
          options={[{ value: '', label: '(none)' }, ...others.map((n) => ({ value: n, label: n }))]}
          onChange={(v) => mutate((l) => (v ? (l.syncedLayer = v) : (delete l.syncedLayer, delete l.syncedMotions)))}
        />
      </PropRow>
      {layer.syncedLayer && (
        <PropRow label="Synced motions" hint='{ "StateName": "models/x.glb#Clip" } replaces the source motion per state'>
          <JsonField value={layer.syncedMotions ?? {}} onChange={(v) => mutate((l) => (v && typeof v === 'object' && Object.keys(v).length ? (l.syncedMotions = v as Layer['syncedMotions']) : delete l.syncedMotions))} />
        </PropRow>
      )}
    </div>
  );
}
